import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Minus, ShoppingCart, CheckCircle2, UtensilsCrossed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/assets";

export default function OrderPage() {
  const { stationId } = useParams();
  const [station, setStation] = useState(null);
  const [menu, setMenu] = useState(null);
  const [started, setStarted] = useState(false);
  const [cust, setCust] = useState({ name: "", mobile: "" });
  const [qty, setQty] = useState({});
  const [notes, setNotes] = useState("");
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/station/${stationId}`).then((r) => setStation(r.data)).catch(() => setStation(false));
    api.get("/menu").then((r) => setMenu(r.data));
  }, [stationId]);

  const setItemQty = (id, d) => setQty((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) + d) }));
  const cartItems = menu ? menu.items.filter((i) => qty[i.id] > 0) : [];
  const total = cartItems.reduce((s, i) => s + i.selling_price * qty[i.id], 0);

  const place = async () => {
    setPlacing(true);
    try {
      const r = await api.post("/orders", {
        station_id: stationId, customer_name: cust.name, mobile: cust.mobile, notes,
        items: cartItems.map((i) => ({ item_id: i.id, qty: qty[i.id] })),
      });
      setDone(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setPlacing(false); }
  };

  if (station === false) return <div className="min-h-screen flex items-center justify-center text-slate-400">Station not found</div>;
  if (!station || !menu) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-purple-400" size={40} /></div>;

  if (done) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="glass rounded-3xl p-8 text-center max-w-md animate-in" data-testid="order-done">
        <CheckCircle2 className="text-emerald-400 mx-auto mb-3" size={48} />
        <h1 className="font-display text-2xl font-bold">Order Placed!</h1>
        <p className="font-mono text-purple-300 mt-2">{done.order_id}</p>
        <p className="text-sm text-slate-400 mt-1">{done.station_name} · ₹{done.total}</p>
        <p className="text-sm text-slate-400 mt-4">Your snacks are being prepared. Please wait at your station.</p>
      </div>
    </div>
  );

  const subsFor = (catId) => menu.subcategories.filter((s) => s.category_id === catId);
  const itemsFor = (catId, subId) => menu.items.filter((i) => i.category_id === catId && (subId ? i.subcategory_id === subId : !i.subcategory_id));

  return (
    <div className="min-h-screen text-slate-100 pb-32">
      <header className="p-4 flex items-center gap-3 border-b border-purple-500/20">
        <img src={LOGO_URL} alt="" className="h-10 w-10 rounded-lg object-cover" />
        <div><p className="font-display font-bold tracking-wide text-sm">FOOD & DRINKS</p><p className="text-xs text-purple-300">{station.name}</p></div>
      </header>

      {!started ? (
        <div className="max-w-md mx-auto px-4 pt-10">
          <div className="glass rounded-2xl p-6">
            <UtensilsCrossed className="text-purple-300 mb-3" size={28} />
            <h2 className="font-display text-xl font-bold mb-1">Order to {station.name}</h2>
            <p className="text-sm text-slate-400 mb-4">Enter your details to start ordering</p>
            <Input data-testid="order-name" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} placeholder="Your name" className="h-12 bg-purple-950/30 border-purple-500/30 mb-3" />
            <Input data-testid="order-mobile" value={cust.mobile} onChange={(e) => setCust({ ...cust, mobile: e.target.value.replace(/[^0-9+]/g, "") })} placeholder="Mobile number" className="h-12 bg-purple-950/30 border-purple-500/30 mb-4" />
            <Button data-testid="order-start-btn" disabled={!cust.name.trim() || cust.mobile.trim().length < 10} onClick={() => setStarted(true)} className="w-full h-12 bg-purple-600 hover:bg-purple-500 font-semibold">View Menu</Button>
          </div>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto px-4 pt-6">
          {menu.categories.length === 0 && <p className="text-center text-slate-500 py-10">Menu coming soon.</p>}
          {menu.categories.map((cat) => (
            <div key={cat.id} className="mb-6">
              <h2 className="font-display text-lg font-bold uppercase tracking-wide text-purple-200 mb-3">{cat.name}</h2>
              {[...subsFor(cat.id).map((s) => [s.name, itemsFor(cat.id, s.id)]), ["", itemsFor(cat.id, "")]].map(([label, its], idx) => (
                its.length > 0 && (
                  <div key={idx} className="mb-3">
                    {label && <p className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2">{label}</p>}
                    <div className="space-y-2">
                      {its.map((i) => (
                        <div key={i.id} data-testid={`menu-item-${i.id}`} className="glass rounded-xl p-3 flex items-center justify-between">
                          <div><p className="font-semibold">{i.name}</p><p className="text-sm text-emerald-400 font-mono">₹{i.selling_price}</p></div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => setItemQty(i.id, -1)} className="w-8 h-8 rounded-lg glass flex items-center justify-center"><Minus size={14} /></button>
                            <span className="w-6 text-center font-mono" data-testid={`qty-${i.id}`}>{qty[i.id] || 0}</span>
                            <button data-testid={`add-item-${i.id}`} onClick={() => setItemQty(i.id, 1)} className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center"><Plus size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          ))}
          {cartItems.length > 0 && <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the café (optional)" className="h-11 bg-purple-950/30 border-purple-500/30 mb-3" />}
        </div>
      )}

      {started && cartItems.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 p-4 backdrop-blur-md bg-purple-950/70 border-t border-purple-500/30">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <div className="flex-1"><p className="text-xs text-slate-400">{cartItems.reduce((s, i) => s + qty[i.id], 0)} items</p><p className="font-display text-xl font-bold text-emerald-400">₹{total}</p></div>
            <Button data-testid="place-order-btn" disabled={placing} onClick={place} className="h-12 px-6 bg-purple-600 hover:bg-purple-500 font-semibold">{placing ? <Loader2 className="animate-spin" /> : <><ShoppingCart size={16} /> Place Order</>}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
